import { useCallback, useEffect, useMemo, useState } from "react";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import { db } from "../firebase";
import { useAccountUid } from "../contexts/UserContext";
import api from "../utils/api";

// New-client registration requests waiting on the groomer.
//
// Only relevant to groomers who review registrations (Settings → Online
// Booking → "Approve new client registrations"). For everyone else the query
// simply returns nothing, so the tab and badge stay hidden.
//
// Reads come straight from Firestore so the list and the sidebar badge update
// the moment a request lands; approve/decline go through the API, because
// approving creates the client record and sends the acceptance email — work
// that belongs on the server. The listener then removes the row on its own.

/** One shared listener per account, reference-counted (sidebar + page). */
const shared = {
  uid: null,
  unsub: null,
  latest: [],
  loaded: false,
  subscribers: new Set(),
};

function publish() {
  shared.subscribers.forEach((fn) => fn(shared.latest, shared.loaded));
}

function subscribe(accountUid, onData) {
  if (shared.uid !== accountUid) {
    if (shared.unsub) shared.unsub();
    shared.uid = accountUid;
    shared.latest = [];
    shared.loaded = false;
    shared.unsub = onSnapshot(
      query(
        collection(db, "clientRegistrations"),
        where("groomerId", "==", accountUid),
        where("status", "==", "pending"),
      ),
      (snap) => {
        shared.latest = snap.docs
          .map((d) => {
            const data = d.data() || {};
            return {
              id: d.id,
              ...data,
              createdAt: data.createdAt?.toDate
                ? data.createdAt.toDate()
                : data.createdAt || null,
            };
          })
          .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
        shared.loaded = true;
        publish();
      },
      (err) => {
        // A badge is not worth surfacing an error for — fail quiet.
        console.warn("[usePendingRegistrations] snapshot error:", err?.message);
        shared.latest = [];
        shared.loaded = true;
        publish();
      },
    );
  }

  shared.subscribers.add(onData);
  if (shared.loaded) onData(shared.latest, true);

  return () => {
    shared.subscribers.delete(onData);
    if (shared.subscribers.size === 0 && shared.unsub) {
      shared.unsub();
      shared.unsub = null;
      shared.uid = null;
      shared.latest = [];
      shared.loaded = false;
    }
  };
}

export default function usePendingRegistrations() {
  const accountUid = useAccountUid();
  const [requests, setRequests] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [busyIds, setBusyIds] = useState(() => new Set());

  useEffect(() => {
    if (!accountUid) {
      setRequests([]);
      setLoaded(true);
      return undefined;
    }
    return subscribe(accountUid, (list, isLoaded) => {
      setRequests(list);
      setLoaded(isLoaded);
    });
  }, [accountUid]);

  const setBusy = useCallback((id, busy) => {
    setBusyIds((prev) => {
      const next = new Set(prev);
      if (busy) next.add(id);
      else next.delete(id);
      return next;
    });
  }, []);

  const approve = useCallback(
    async (request) => {
      setBusy(request.id, true);
      try {
        const { data } = await api.post(
          `/registration/requests/${request.id}/approve`,
        );
        const name = request.ownerName || "Client";
        if (data?.emailed) {
          return {
            ok: true,
            message: `${name} approved — booking link emailed.`,
          };
        }
        return {
          ok: true,
          severity: "warning",
          // Approving still worked; the client just won't have the link, which
          // the groomer needs to know so they can send it themselves.
          message: request.email
            ? `${name} approved, but the email didn't send. Send them your booking link.`
            : `${name} approved. No email on file — send them your booking link.`,
        };
      } catch (err) {
        return {
          ok: false,
          message:
            err?.response?.data?.error || "Could not approve this request.",
        };
      } finally {
        setBusy(request.id, false);
      }
    },
    [setBusy],
  );

  const decline = useCallback(
    async (request, reason = "") => {
      setBusy(request.id, true);
      try {
        await api.post(`/registration/requests/${request.id}/decline`, {
          reason,
        });
        return {
          ok: true,
          message: `${request.ownerName || "Request"} declined.`,
        };
      } catch (err) {
        return {
          ok: false,
          message:
            err?.response?.data?.error || "Could not decline this request.",
        };
      } finally {
        setBusy(request.id, false);
      }
    },
    [setBusy],
  );

  return useMemo(
    () => ({
      requests,
      count: requests.length,
      loaded,
      approve,
      decline,
      isBusy: (id) => busyIds.has(id),
    }),
    [requests, loaded, approve, decline, busyIds],
  );
}
